import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  } from "@mui/material";
import { CurrencyInput, PercentInput } from './StandardInput';
import { InfoBox, FormRow } from './StandardLayout';
import { colors } from "../theme";
import { Expense } from "../utils/interface";
import { computeCostBreakdown, parseNumericField } from "../utils/projectCostCalcs";

import { ConstructionLoader } from './ConstructionLoader';
const formatCurrencySafe = (value: any) => {
  if (value === undefined || value === null || value === "" || value === "N/A") return "—";
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (!Number.isFinite(num)) return String(value);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
};

const formatPercentSafe = (value: number | undefined | null): string => {
  if (value === undefined || value === null || !Number.isFinite(value)) return "—";
  return `${(value * 100).toFixed(1)}%`;
};

/** Parse a numeric value from a string that may contain $, commas, or % */
const parseNumeric = (val: any): number | undefined => {
  if (val === undefined || val === null || val === "" || val === "N/A") return undefined;
  const num = Number(String(val).replace(/[$,%\s]/g, ""));
  return Number.isFinite(num) ? num : undefined;
};

/** Three-column summary row: label | dollar amount | percentage */
const SummaryRow = ({
  label,
  amount,
  percent,
  labelColor,
  bold,
}: {
  label: string;
  amount: string;
  percent: string;
  labelColor?: string;
  bold?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      py: 1,
      borderBottom: `1px solid ${colors.grey[300]}`,
    }}
  >
    <Typography
      variant="body2"
      sx={{
        flex: 1,
        fontWeight: bold ? 700 : 600,
        fontSize: "0.8rem",
        color: labelColor || colors.grey[900],
      }}
    >
      {label}
    </Typography>
    <Typography className="num"
      variant="body2"
      sx={{ width: 110, textAlign: "right", fontWeight: bold ? 700 : 600, fontSize: "0.8rem" }}
    >
      {amount}
    </Typography>
    <Typography className="num"
      variant="body2"
      sx={{ width: 65, textAlign: "right", fontWeight: bold ? 700 : 600, fontSize: "0.8rem" }}
    >
      {percent}
    </Typography>
  </Box>
);

export default function SecondLienSection({
  modelDetails,
  handleFieldChange,
  variables,
  finalMetricsCalculating,
  expenses = [],
}: {
  modelDetails: any;
  handleFieldChange: (fieldId: string, field_key: string, value: string | number) => void;
  variables: any;
  finalMetricsCalculating: boolean;
  expenses?: Expense[];
}) {
  const getFieldValue = (field_key: string, defaultValue: any) => {
    const field = modelDetails?.user_model_field_values?.find((f: any) => f.field_key === field_key);
    return field?.value ?? defaultValue;
  };

  const getFieldId = (field_key: string) => {
    const field = modelDetails?.user_model_field_values?.find((f: any) => f.field_key === field_key);
    return field?.field_id ?? "";
  };

  const [loanAmount, setLoanAmount] = useState(() =>
    getFieldValue("Pref. Equity / Mezz. Loan Amount", "")
  );
  const [interestRate, setInterestRate] = useState(() => getFieldValue("Interest Rate (Accrual)", 5));
  const [participation, setParticipation] = useState(() => getFieldValue("Participation", 10));
  const [loanName, setLoanName] = useState(() => getFieldValue("Loan Name", "Pref / Mezz Loan"));

  const SECOND_LIEN_FIELD_KEYS = useMemo(
    () =>
      [
        "Pref. Equity / Mezz. Loan Amount",
        "Interest Rate (Accrual)",
        "Participation",
        "Loan Name",
      ] as const,
    []
  );

  const secondLienValuesFingerprint = useMemo(() => {
    const umfv = modelDetails?.user_model_field_values;
    if (!Array.isArray(umfv)) return "";
    return SECOND_LIEN_FIELD_KEYS.map((k) => {
      const f = umfv.find((x: any) => x.field_key === k);
      return `${k}=${f?.value ?? ""}`;
    }).join("|");
  }, [modelDetails?.user_model_field_values, SECOND_LIEN_FIELD_KEYS]);

  useEffect(() => {
    setLoanAmount(getFieldValue("Pref. Equity / Mezz. Loan Amount", ""));
    setInterestRate(getFieldValue("Interest Rate (Accrual)", 5));
    setParticipation(getFieldValue("Participation", 10));
    setLoanName(getFieldValue("Loan Name", "Pref / Mezz Loan"));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- secondLienValuesFingerprint tracks these fields in modelDetails
  }, [secondLienValuesFingerprint]);

  const pushField = (field_key: string, value: string | number) => {
    handleFieldChange(getFieldId(field_key), field_key, value);
  };

  // ---------- Derived values for Second Lien Summary ----------
  const displayName = loanName?.trim() || "Pref / Mezz Loan";
  const prefMezzNum = parseNumeric(loanAmount);

  // Read Sr. Exact Loan Amount from modelDetails (always available, not dependent on variables)
  const srLoanSize = useMemo(() => {
    const field = modelDetails?.user_model_field_values?.find(
      (f: any) => f.field_key && f.field_key.trim() === "Sr. Cons: Exact Loan Amount",
    );
    const raw = Number(String(field?.value ?? 0).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(raw) && raw > 0 ? raw : 0;
  }, [modelDetails]);

  // Interest reserve is part of Total Project Costs here too. It was previously
  // omitted on this card only, so the Second Lien LTC disagreed with the Senior
  // Construction Loan LTC on the same model.
  const interestReserve = useMemo(
    () => parseNumericField(variables?.["Sr. Cons. Estimated Interest Reserve"]),
    [variables],
  );

  // Shared with ConstructionLoanSummary via utils/projectCostCalcs so the two
  // cards cannot drift, and so gross buildable SF resolves past the duplicate
  // "Gross Buildable Square Feet" field on the Development model type.
  // Only the total is rendered here; the per-category figures live on the
  // Senior Construction Loan card.
  const { totalProjectCosts } = useMemo(
    () =>
      computeCostBreakdown({
        expenses,
        modelDetails,
        constructionLoanAmount: srLoanSize,
        prefMezzAmount: prefMezzNum ?? 0,
        interestReserve,
      }),
    [expenses, modelDetails, srLoanSize, prefMezzNum, interestReserve],
  );

  const derived = useMemo(() => {
    if (totalProjectCosts <= 0 || srLoanSize <= 0) {
      return { srLoanLtc: undefined, prefMezzPct: undefined, lastDollarAmount: undefined, lastDollarPct: undefined };
    }
    const srLoanLtc = srLoanSize / totalProjectCosts;
    const prefMezzPct = prefMezzNum ? prefMezzNum / totalProjectCosts : undefined;
    const lastDollarAmount = prefMezzNum ? srLoanSize + prefMezzNum : undefined;
    const lastDollarPct = prefMezzPct !== undefined ? srLoanLtc + prefMezzPct : undefined;
    return { srLoanLtc, prefMezzPct, lastDollarAmount, lastDollarPct };
  }, [srLoanSize, totalProjectCosts, prefMezzNum]);

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 4 }, pb: 4 }}>
      <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, gap: 3, alignItems: 'flex-start' }}>

        {/* LEFT COLUMN: Inputs (65%) */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 65%' }, minWidth: 0 }}>
          <Card elevation={0} sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Loan Terms
              </Typography>

              <FormRow sx={{ mb: 2 }}>
                <TextField
                  label="Loan Name"
                  value={loanName}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLoanName(v);
                    pushField("Loan Name", v);
                  }}
                  size="small"
                  fullWidth
                />
              </FormRow>


              <FormRow sx={{ mb: 2 }}>
                <CurrencyInput
                  label={`${displayName} Amount`}
                  value={loanAmount}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLoanAmount(v);
                    pushField("Pref. Equity / Mezz. Loan Amount", v);
                  }}
                  fullWidth
                />
               
              </FormRow>

              <FormRow>
              <PercentInput
                  label="Interest Rate (Accrual)"
                  value={interestRate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInterestRate(v);
                    pushField("Interest Rate (Accrual)", v);
                  }}
                  fullWidth
                />
                <PercentInput
                  label="Participation"
                  value={participation}
                  onChange={(e) => {
                    const v = e.target.value;
                    setParticipation(v);
                    pushField("Participation", v);
                  }}
                  fullWidth
                />
              </FormRow>
            </CardContent>
          </Card>
        </Box>

        {/* RIGHT COLUMN: Sticky Summary (32%) */}
        <Box
          sx={{
            flex: { xs: '1 1 100%', md: '0 0 32%' },
            minWidth: 0,
            position: { xs: 'static', md: 'sticky' },
            top: 20,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
          }}
        >
          <Card
            elevation={0}
            sx={{
              backgroundColor: colors.white,
              border: `1px solid ${colors.grey[300]}`,
              borderRadius: 2,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {finalMetricsCalculating && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.68)',
                  backdropFilter: 'blur(2px)',
                }}
              >
                <ConstructionLoader size={32} label="Updating summary" />
                <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[700] }}>
                  Updating summary...
                </Typography>
              </Box>
            )}
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {`${displayName} Summary`}
              </Typography>

              {/* Primary metric — header mirrors live Loan Name */}
              <Box sx={{ mb: 3 }}>
                <InfoBox
                  label={`${displayName.toUpperCase()} LOAN SIZE`}
                  value={formatCurrencySafe(loanAmount)}
                  variant="primary"
                />
              </Box>

              {/* Column headers */}
              <Box sx={{ display: "flex", alignItems: "baseline", pb: 0.5, borderBottom: `2px solid ${colors.grey[300]}` }}>
                <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, fontSize: "0.65rem", color: colors.grey[600] }}>
                  Source
                </Typography>
                <Typography variant="caption" sx={{ width: 110, textAlign: "right", fontWeight: 700, fontSize: "0.65rem", color: colors.grey[600] }}>
                  Amount
                </Typography>
                <Typography variant="caption" sx={{ width: 65, textAlign: "right", fontWeight: 700, fontSize: "0.65rem", color: colors.grey[600] }}>
                  LTC
                </Typography>
              </Box>

              {/* Row 1 — Sr. Construction Loan */}
              <SummaryRow
                label="Sr. Construction Loan"
                amount={srLoanSize > 0 ? formatCurrencySafe(srLoanSize) : "—"}
                percent={derived.srLoanLtc !== undefined ? formatPercentSafe(derived.srLoanLtc) : "—"}
              />

              {/* Row 2 — Pref / Mezz */}
              <SummaryRow
                label={displayName}
                amount={prefMezzNum !== undefined ? formatCurrencySafe(prefMezzNum) : "—"}
                percent={derived.prefMezzPct !== undefined ? formatPercentSafe(derived.prefMezzPct) : "—"}
                labelColor={prefMezzNum !== undefined && prefMezzNum < 0 ? colors.error : undefined}
              />

              {/* Row 3 — Last Dollar (bold) */}
              <SummaryRow
                label={`Last Dollar of ${displayName}`}
                amount={derived.lastDollarAmount !== undefined ? formatCurrencySafe(derived.lastDollarAmount) : "—"}
                percent={derived.lastDollarPct !== undefined ? formatPercentSafe(derived.lastDollarPct) : "—"}
                labelColor={derived.lastDollarAmount !== undefined && derived.lastDollarAmount < 0 ? colors.error : undefined}
                bold
              />

              {/* Caption */}
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 2, color: colors.grey[600], fontStyle: "italic", fontSize: "0.7rem" }}
              >
                Estimated LTC. Please review after establishing budgets.
              </Typography>
            </CardContent>
          </Card>
        </Box>

      </Box>
    </Box>
  );
}
